/**
 * Coeficientes do motor de cálculo (docs 03–06/10/11), parametrizáveis e versionados.
 * Nenhum número mágico vive no código de cálculo — tudo nasce daqui (doc 13.7).
 */

import type { Eixo } from "./types.ts";

/** Forma de agregar os três subíndices no IMGQ bruto. */
export type PonderacaoImgq = "simples" | "ponderadaPorItens";

export interface ConfigMotor {
  /** Curva ONA: score por nível de maturidade (0–4). */
  readonly curvaScore: Readonly<Record<"0" | "1" | "2" | "3" | "4", number>>;
  /** Risco residual. */
  readonly residualReducao: number; // controle reduz no máx. 70%
  readonly residualPiso: number; // risco irredutível (30% do inerente)
  /** Limiares de risco (P×I / residual). */
  readonly riskRP: number;
  readonly riskRM: number;
  readonly riskRA: number;
  readonly gatilhoPlano: number; // risco ≥ valor prioriza o item no plano de ação
  /** IMGQ. */
  readonly metaSubindice: number; // meta institucional por eixo (0,46)
  readonly exclusaoAtiva: boolean; // regra de exclusão CORE liga/desliga
  readonly tetoExclusao: number; // teto quando travado (0,599)
  readonly ponderacao: PonderacaoImgq; // doc 11: ponderada por itens é a aprovada
  /**
   * Override fixo do nº de itens por eixo na ponderação. Quando `null` (padrão),
   * os pesos são derivados do nº real de itens de cada eixo no catálogo ativo —
   * implementação fiel ao princípio "média ponderada pelo nº de itens" e robusta
   * a mudanças de versão do catálogo. O doc 11 cita 62/146/39 (contagem de uma
   * versão anterior); o catálogo v4.0 atual tem 80/111/56.
   */
  readonly pesoEixos: Readonly<Record<Eixo, number>> | null;
  /** Ajuste final (só na avaliação final). */
  readonly qaciFaixa: number; // ±5pp
  readonly imsFaixa: number; // ±3pp (neutro em 0,5)
  readonly qaciC1: number;
  readonly qaciC2: number;
  readonly qaciC3: number;
  /** Selo. */
  readonly seloBronze: number;
  readonly seloPrata: number;
  readonly seloOuro: number;
}

/**
 * Configuração padrão v4.0. Espelha `DEFAULT_CONFIG` do protótipo, exceto a
 * ponderação do IMGQ: o doc 11 (Parecer Técnico, Mai/2026) aprovou a média
 * ponderada pelo nº de itens — `(IMGO·62 + IMQA·146 + IMGR·39)/247` —, então o
 * padrão é `ponderadaPorItens`. Use `ponderacao: "simples"` para reproduzir
 * avaliações anteriores à mudança.
 */
export const CONFIG_PADRAO: ConfigMotor = Object.freeze({
  curvaScore: Object.freeze({ "0": 0, "1": 0.2, "2": 0.5, "3": 0.8, "4": 1 }),
  residualReducao: 0.7,
  residualPiso: 0.3,
  riskRP: 3,
  riskRM: 8,
  riskRA: 14,
  gatilhoPlano: 8,
  metaSubindice: 0.46,
  exclusaoAtiva: true,
  tetoExclusao: 0.599,
  ponderacao: "ponderadaPorItens",
  pesoEixos: null,
  qaciFaixa: 0.05,
  imsFaixa: 0.06,
  qaciC1: 0.5,
  qaciC2: 0.3,
  qaciC3: 0.2,
  seloBronze: 0.46,
  seloPrata: 0.6,
  seloOuro: 0.85,
});
